import React, { useEffect, useMemo, useState, useRef } from "react";
import { Tag, Input, Tabs, Spin, Button, Modal } from "antd";
import Table from "../../../../core/common/dataTable/index";
import { Link, useNavigate } from "react-router-dom";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { Dropdown } from "react-bootstrap";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getAllRegularizations,
  RegularizationRecord,
  updateRegularizationStatus,
  getRegularizationWidgets,
  RegularizationWidgets,
} from "../../../../core/api/regularization";
import { useAuthToken } from "../../../../core/data/redux/useAuthToken";
import useGetEmployees from "../../../../core/api/useGetEmployees";
import { API_BASE_PATH } from "../../../../environment";
import {
  getAllAttendance,
  AttendanceRecord,
  getAttendanceWidgets,
  AttendanceWidgetStats,
} from "../../../../core/api/useAttendance";
import { toast } from "react-toastify";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faHouse,
  faLaptopHouse,
  faPersonWalkingLuggage,
  faClock,
  faCircleCheck,
  faCircleXmark,
  faSuitcase,
  faDollarSign,
  faCalendarCheck,
  faFileText,
} from "@fortawesome/free-solid-svg-icons";

const routes = all_routes;
const { TabPane } = Tabs;
const { TextArea } = Input;

const statusColors: Record<string, string> = {
  Present: "green",
  Absent: "red",
  Leave: "gold",
  Late: "orange",
};

const TimeSheetDash = () => {
  const BASE_PATH = API_BASE_PATH;
  const empToken = useAuthToken();
  const navigate = useNavigate();
  // States
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [dateFilter, setDateFilter] = useState<string>("All");
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [regularizationSearchText, setRegularizationSearchText] = useState("");
  const [regularizations, setRegularizations] = useState<
    RegularizationRecord[]
  >([]);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [attendanceWidgets, setAttendanceWidgets] =
    useState<AttendanceWidgetStats | null>(null);
  const [regularizationWidgets, setRegularizationWidgets] =
    useState<RegularizationWidgets | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"month" | "date">("date");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const { employees, isLoading: loadingEmployees } = useGetEmployees();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRecord, setSelectedRecord] =
    useState<RegularizationRecord | null>(null);
  const [employeeImages, setEmployeeImages] = useState<Record<string, string>>(
    {}
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const departments = useMemo(() => {
    const deptSet = new Set<string>();
    employees.forEach((emp) => {
      if (emp.department_name) deptSet.add(emp.department_name);
    });
    return ["All", ...Array.from(deptSet)];
  }, [employees]);

  // Helper functions
  const formatTime = (timeString: string | null) => {
    if (!timeString) return "Pending";
    try {
      return dayjs(timeString).format("hh:mm A");
    } catch {
      return timeString;
    }
  };

  const formatDate = (dateValue: number | string) => {
    if (!dateValue) return "--";
    try {
      const date =
        typeof dateValue === "number"
          ? dayjs(dateValue * 1000)
          : dayjs(dateValue);
      return date.format("YYYY-MM-DD");
    } catch {
      return String(dateValue);
    }
  };

  // Employee name mapping
  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};
    employees.forEach((emp) => {
      map[emp.employee_id] = {
        name: `${emp.firstname} ${emp.lastname}`,
        department: emp.department_name || "Unknown",
        role: emp.role_name || "Unknown",
      };
    });
    return map;
  }, [employees]);

  // Fetch employee images
  const fetchEmployeeImage = async (employeeId: string) => {
    try {
      const response = await axios.get(
        `${BASE_PATH}/register/employee/${employeeId}/image`,
        {
          headers: {
            Authorization: `Bearer ${empToken}`,
          },
          responseType: "blob",
        }
      );
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error(`Error fetching image for ${employeeId}:`, error);
      return "/assets/img/defaultavatar.png";
    }
  };

  // Fetch images when data changes
  useEffect(() => {
    const fetchImages = async () => {
      const uniqueIds = new Set([
        ...attendanceData.map((item) => item.employee_id),
        ...regularizations.map((item) => item.employee_id),
      ]);

      const imagePromises = Array.from(uniqueIds).map(async (id) => {
        if (!employeeImages[id.toString()]) {
          const url = await fetchEmployeeImage(id.toString());
          setEmployeeImages((prev) => ({ ...prev, [id.toString()]: url }));
        }
      });

      await Promise.all(imagePromises);
    };

    if (empToken && (attendanceData.length > 0 || regularizations.length > 0)) {
      fetchImages();
    }
  }, [attendanceData, regularizations, empToken]);

  // Fetch attendance data
  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (!empToken) return;
      setLoading(true);
      try {
        const data = await getAllAttendance(empToken);
        setAttendanceData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching attendance:", error);
        setAttendanceData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAttendanceData();
  }, [empToken]);

  // Fetch regularizations
  useEffect(() => {
    const fetchRegularizations = async () => {
      if (!empToken) return;
      try {
        const data = await getAllRegularizations(empToken);
        setRegularizations(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching regularizations:", error);
        setRegularizations([]);
      }
    };
    fetchRegularizations();
  }, [empToken]);

  // Fetch attendance widgets data
  useEffect(() => {
    const fetchWidgetsData = async () => {
      if (!empToken) return;
      try {
        const data = await getAttendanceWidgets(
          selectedMonth + 1,
          selectedYear,
          selectedDate ? selectedDate.format("YYYY-MM-DD") : null,
          viewMode === "date" && selectedDate ? selectedDate.date() : null,
          null,
          empToken
        );
        setAttendanceWidgets(data);
      } catch (error) {
        console.error("Error fetching attendance widgets:", error);
        setAttendanceWidgets(null);
      }
    };
    fetchWidgetsData();
  }, [empToken, selectedMonth, selectedYear, selectedDate, viewMode]);

  // Fetch regularization widgets data
  useEffect(() => {
    const fetchRegularizationWidgets = async () => {
      if (!empToken) return;
      try {
        const data = await getRegularizationWidgets(
          selectedMonth + 1,
          selectedYear,
          viewMode === "date" && selectedDate ? selectedDate.date() : null,
          null,
          empToken
        );
        setRegularizationWidgets(data);
      } catch (error) {
        console.error("Error fetching regularization widgets:", error);
        setRegularizationWidgets(null);
      }
    };
    fetchRegularizationWidgets();
  }, [empToken, selectedMonth, selectedYear, selectedDate, viewMode]);

  // Attendance Widget Data
  const attendanceWidgetData = useMemo(() => {
    if (!attendanceWidgets) {
      return [];
    }

    return [
      {
        id: "present",
        icon: "ti ti-user-check",
        bg: "rgba(22, 163, 74, 0.1)",
        title: "Present",
        type: "present",
        totalApplied: attendanceWidgets.present || 0,
        color: "success",
        totalEmployees: attendanceWidgets?.all_employee || 0,
        presentEmployees: attendanceWidgets?.present || 0,
        absentEmployees: 0,
      },
      {
        id: "absent",
        icon: "ti ti-user-x",
        bg: "rgba(220, 38, 38, 0.1)",
        title: "Absent",
        type: "absent",
        totalApplied: attendanceWidgets.absent || 0,
        color: "danger",
        totalEmployees: attendanceWidgets?.all_employee || 0,
        presentEmployees: 0,
        absentEmployees: attendanceWidgets?.absent || 0,
      },
      {
        id: "late",
        icon: "ti ti-clock",
        bg: "rgba(245, 158, 11, 0.1)",
        title: "Late",
        type: "late",
        totalApplied: attendanceWidgets.late || 0,
        color: "warning",
        totalEmployees: attendanceWidgets?.all_employee || 0,
        presentEmployees: attendanceWidgets?.present || 0,
        absentEmployees: attendanceWidgets?.absent || 0,
      },
      {
        id: "early",
        icon: "ti ti-user-minus",
        bg: "rgba(59, 130, 246, 0.1)",
        title: "Early",
        type: "early",
        totalApplied: attendanceWidgets.earlyout || 0,
        color: "info",
        totalEmployees: attendanceWidgets?.all_employee || 0,
        presentEmployees: attendanceWidgets?.present || 0,
        absentEmployees: attendanceWidgets?.absent || 0,
      },
      {
        id: "missed_punchout",
        icon: "ti ti-clock-x",
        bg: "rgba(219, 39, 119, 0.1)",
        title: "Missed Punch Out",
        type: "missed_punchout",
        totalApplied: attendanceWidgets.missed_punchout || 0,
        color: "primary",
        totalEmployees: attendanceWidgets?.all_employee || 0,
        presentEmployees: attendanceWidgets?.present || 0,
        absentEmployees: attendanceWidgets?.absent || 0,
      },
      {
        id: "device",
        icon: "ti ti-device-laptop",
        bg: "rgba(22, 163, 74, 0.1)",
        title: "Device",
        type: "device",
        totalApplied: attendanceWidgets.device || 0,
        color: "success",
        totalEmployees: attendanceWidgets?.all_employee || 0,
        presentEmployees: attendanceWidgets?.present || 0,
        absentEmployees: attendanceWidgets?.absent || 0,
      },
      {
        id: "online",
        icon: "ti ti-wifi",
        bg: "rgba(245, 158, 11, 0.1)",
        title: "Online",
        type: "online",
        totalApplied: attendanceWidgets.online || 0,
        color: "warning",
        totalEmployees: attendanceWidgets?.all_employee || 0,
        presentEmployees: attendanceWidgets?.present || 0,
        absentEmployees: attendanceWidgets?.absent || 0,
      },
    ];
  }, [attendanceWidgets]);

  // Update scroll button states
  useEffect(() => {
    const updateScrollState = () => {
      if (!scrollRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };

    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", updateScrollState);
      updateScrollState();
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", updateScrollState);
      }
    };
  }, [attendanceWidgetData]);

  // Scroll handler
  const scrollWidgets = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const scrollAmount = 300;
    scrollRef.current.scrollLeft +=
      direction === "right" ? scrollAmount : -scrollAmount;
  };

  // Filter attendance data based on all filters
  const filteredAttendanceData = useMemo(() => {
    let data = Array.isArray(attendanceData) ? [...attendanceData] : [];

    if (viewMode === "month") {
      data = data.filter((item) => {
        const itemDate =
          typeof item.date === "number"
            ? dayjs(item.date * 1000)
            : dayjs(item.date);
        return (
          itemDate.month() === selectedMonth && itemDate.year() === selectedYear
        );
      });
    } else if (viewMode === "date" && selectedDate) {
      const targetDate = selectedDate.format("YYYY-MM-DD");
      data = data.filter((item) => {
        const itemDate = formatDate(item.date);
        return itemDate === targetDate;
      });
    }

    if (dateFilter !== "All") {
      const today = dayjs();
      let fromDate = today;

      switch (dateFilter) {
        case "Today":
          fromDate = today;
          break;
        case "Yesterday":
          fromDate = today.subtract(1, "day");
          break;
        case "Last Week":
          fromDate = today.subtract(7, "day");
          break;
        case "Last Month":
          fromDate = today.subtract(30, "day");
          break;
      }

      data = data.filter((item) => {
        const itemDate = dayjs(formatDate(item.date));
        return (
          itemDate.isAfter(fromDate.subtract(1, "day")) ||
          itemDate.isSame(fromDate, "day")
        );
      });
    }

    if (searchText) {
      const lower = searchText.toLowerCase();
      data = data.filter((item) => {
        const employee = employeeNameMap[item.employee_id];
        const text = `${employee?.name || ""} ${item.username || ""} ${
          item.status || ""
        }`.toLowerCase();
        return text.includes(lower);
      });
    }

    if (selectedDepartment !== "All") {
      data = data.filter(
        (item) =>
          employeeNameMap[item.employee_id]?.department === selectedDepartment
      );
    }

    return data;
  }, [
    attendanceData,
    viewMode,
    selectedMonth,
    selectedYear,
    selectedDate,
    dateFilter,
    searchText,
    selectedDepartment,
    employeeNameMap,
  ]);

  // Filter regularization data
  const filteredRegData = useMemo(() => {
    let data = [...regularizations];

    if (viewMode === "month") {
      data = data.filter((item) => {
        const itemDate = dayjs(item.timesheet_date);
        return (
          itemDate.month() === selectedMonth && itemDate.year() === selectedYear
        );
      });
    } else if (viewMode === "date" && selectedDate) {
      const targetDate = selectedDate.format("YYYY-MM-DD");
      data = data.filter((item) => {
        const itemDate = dayjs(item.timesheet_date).format("YYYY-MM-DD");
        return itemDate === targetDate;
      });
    }

    if (regularizationSearchText) {
      const lower = regularizationSearchText.toLowerCase();
      data = data.filter((item) => {
        const employeeName =
          employeeNameMap[item.employee_id]?.name || item.employee_id;
        const text =
          `${employeeName} ${item.timesheet_date} ${item.reg_type} ${item.reason} ${item.status}`.toLowerCase();
        return text.includes(lower);
      });
    }

    return data;
  }, [
    regularizations,
    viewMode,
    selectedMonth,
    selectedYear,
    selectedDate,
    regularizationSearchText,
    employeeNameMap,
  ]);

  // Filter regularization data based on status
  const filteredRegDataByStatus = useMemo(() => {
    return (status: string) => {
      let data = [...regularizations];

      if (viewMode === "month") {
        data = data.filter((item) => {
          const itemDate = dayjs(item.timesheet_date);
          return (
            itemDate.month() === selectedMonth &&
            itemDate.year() === selectedYear
          );
        });
      } else if (viewMode === "date" && selectedDate) {
        const targetDate = selectedDate.format("YYYY-MM-DD");
        data = data.filter((item) => {
          const itemDate = dayjs(item.timesheet_date).format("YYYY-MM-DD");
          return itemDate === targetDate;
        });
      }

      if (status !== "All") {
        data = data.filter((item) => item.status === status);
      }

      if (regularizationSearchText) {
        const lower = regularizationSearchText.toLowerCase();
        data = data.filter((item) => {
          const employeeName =
            employeeNameMap[item.employee_id]?.name || item.employee_id;
          const text =
            `${employeeName} ${item.timesheet_date} ${item.reg_type} ${item.reason} ${item.status}`.toLowerCase();
          return text.includes(lower);
        });
      }

      return data;
    };
  }, [
    regularizations,
    viewMode,
    selectedMonth,
    selectedYear,
    selectedDate,
    regularizationSearchText,
    employeeNameMap,
  ]);

  // Calculate attendance stats
  const attendanceStats = useMemo(() => {
    const totalPresent = filteredAttendanceData.filter(
      (r) => r.status === "Present"
    ).length;
    const totalAbsent = filteredAttendanceData.filter(
      (r) => r.status === "Absent"
    ).length;
    const lateLogin = filteredAttendanceData.filter(
      (r) => r.late && r.late !== "0m" && r.late !== "--"
    ).length;

    return {
      totalPresent,
      totalAbsent,
      lateLogin,
      totalEmployees: employees.length,
    };
  }, [filteredAttendanceData, employees]);

  // Handle Approve click
  const handleApproveClick = async (record: RegularizationRecord) => {
    if (!empToken) return;

    try {
      await updateRegularizationStatus(
        {
          reg_id: record.reg_id,
          status: "Approved",
        },
        empToken
      );
      setRegularizations((prev) =>
        prev.map((reg) =>
          reg.reg_id === record.reg_id ? { ...reg, status: "Approved" } : reg
        )
      );
      toast.success("Regularization approved successfully");
    } catch (error) {
      console.error("Error approving regularization:", error);
    }
  };

  // Handle Reject click
  const handleRejectClick = (record: RegularizationRecord) => {
    setSelectedRecord(record);
    setRejectModalOpen(true);
  };

  // Handle Reject submit
  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      toast.warning("Please enter a rejection reason");
      return;
    }

    if (!selectedRecord || !empToken) return;

    try {
      await updateRegularizationStatus(
        {
          reg_id: selectedRecord.reg_id,
          status: "Rejected",
          reject_reason: rejectReason,
        },
        empToken
      );
      setRegularizations((prev) =>
        prev.map((reg) =>
          reg.reg_id === selectedRecord.reg_id
            ? { ...reg, status: "Rejected", reject_reason: rejectReason }
            : reg
        )
      );
      setRejectModalOpen(false);
      setRejectReason("");
      setSelectedRecord(null);
      toast.success("Regularization rejected successfully");
    } catch (error) {
      console.error("Error rejecting regularization:", error);
    }
  };

  // Check if date is Sunday (week off)
  const isWeekOff = (date: dayjs.Dayjs) => {
    return date.day() === 0;
  };

  // Helper functions
  const getShiftIcon = (shiftName: any) => {
    const name = shiftName.toLowerCase();
    if (name.includes("morning")) return "ti ti-sunrise";
    if (name.includes("night")) return "ti ti-moon-stars";
    if (name.includes("evening")) return "ti ti-sun";
    if (name.includes("general")) return "ti ti-calendar-time";
    return "ti ti-clock";
  };

  const getShiftColor = (shiftName: any) => {
    const name = shiftName.toLowerCase();
    if (name.includes("morning")) return "warning";
    if (name.includes("night")) return "dark";
    if (name.includes("evening")) return "primary";
    if (name.includes("general")) return "success";
    return "info";
  };

  // Dynamic data mapping
  const pendingRequestsData = useMemo(() => {
    if (!attendanceWidgets?.shift_assign_count) {
      return [];
    }

    return Object.entries(attendanceWidgets.shift_assign_count).map(
      ([shiftName, totalEmployees]) => ({
        title: shiftName,
        type: "Shift",
        totalEmployees: totalEmployees,
        absent: attendanceWidgets.shift_absent?.[shiftName] || 0,
        present: attendanceWidgets.shift_present?.[shiftName] || 0,
        iconClass: getShiftIcon(shiftName),
        color: getShiftColor(shiftName),
      })
    );
  }, [attendanceWidgets]);

  // Regularization Widget Data
  const regularizationWidgetData = useMemo(() => {
    if (!regularizationWidgets) {
      return [];
    }

    return [
      {
        title: "Total Applied",
        type: "All",
        count: regularizationWidgets.applied || 0,
        icon: <FontAwesomeIcon icon={faPersonWalkingLuggage} />,
        color: "#4ecdc4",
      },
      {
        title: "Pending Request",
        type: "Pending",
        count: regularizationWidgets.pending || 0,
        icon: <FontAwesomeIcon icon={faClock} />,
        color: "#f39c12",
      },
      {
        title: "Approved",
        type: "Approved",
        count: regularizationWidgets.approved || 0,
        icon: <FontAwesomeIcon icon={faCircleCheck} />,
        color: "#45d178ff",
      },
      {
        title: "Rejected",
        type: "Rejected",
        count: regularizationWidgets.rejected || 0,
        icon: <FontAwesomeIcon icon={faCircleXmark} />,
        color: "#ff6b6b",
      },
    ];
  }, [regularizationWidgets]);

  // Handle Pending View click
  const handlePendingView = (type: string) => {
    setActiveTab("2");
    setRegularizationSearchText("");
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Table columns for attendance
  const attendanceColumns: ColumnsType<AttendanceRecord> = [
    {
      title: "Employee",
      key: "employee_id",
      width: 200,
      render: (_, record: AttendanceRecord) => {
        const employee = employeeNameMap[record.employee_id];
        const imageUrl =
          employeeImages[record.employee_id] || "/assets/img/defaultavatar.png";

        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt="Employee"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/assets/img/defaultavatar.png";
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{employee?.name || "Unknown Name"}</b>
              </p>
              <span className="fs-12">
                {employee?.department || record.employee_id}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Date",
      key: "date",
      width: 120,
      render: (_: any, record: AttendanceRecord) => {
        const rawDate =
          record.attendance_date || record.date || record.created_date;
        if (!rawDate) return "--";

        const dateObj = dayjs(rawDate);
        const formattedDate = dateObj.format("DD-MM-YYYY");
        const dayShort = dateObj.format("ddd");

        let badgeColor = "#1763AD";
        if (isWeekOff(dateObj)) {
          badgeColor = "#fd7e14";
        } else if (dayShort === "Sat") {
          badgeColor = "#fd0d91ff";
        }

        return (
          <div className="text-center d-flex flex-column align-items-center gap-1">
            <div style={{ fontWeight: 500 }}>{formattedDate}</div>
            <span
              className="badge"
              style={{
                backgroundColor: badgeColor,
                color: "#fff",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "10px",
              }}
            >
              {dayShort}
            </span>
          </div>
        );
      },
    },
    {
      title: "Check In & Out",
      key: "check",
      width: 130,
      align: "center" as const,
      render: (record: AttendanceRecord) => {
        const isPunch = Boolean(record.punch_in || record.punch_out);
        const checkIn = isPunch ? record.punch_in : record.check_in;
        const checkOut = isPunch ? record.punch_out : record.check_out;

        const getBadge = (iconClass: string, timeValue: string | null) => (
          <div
            className={`badge ${
              iconClass.includes("logout")
                ? "badge bg-danger-subtle text-danger"
                : "badge bg-success-subtle text-success"
            }`}
            style={{
              fontSize: "10px",
              padding: "6px 10px",
              width: "100px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className={`ti ${iconClass}`} style={{ fontSize: "12px" }} />
            <span style={{ fontSize: "11px", fontWeight: 500 }}>
              {timeValue ? formatTime(timeValue) : "Pending"}
            </span>
          </div>
        );

        return (
          <div className="d-flex flex-column align-items-center gap-1">
            {getBadge(isPunch ? "ti-fingerprint" : "ti-device-laptop", checkIn)}
            {getBadge("ti-logout", checkOut)}
          </div>
        );
      },
    },
    {
      title: "Production",
      key: "production_hr",
      width: 120,
      render: (_, record: AttendanceRecord) => (
        <span className="d-inline-flex align-items-center">
          <span
            className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
            style={{
              width: "20px",
              height: "20px",
              minWidth: "20px",
              minHeight: "20px",
              maxWidth: "20px",
              maxHeight: "20px",
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <i className="ti ti-clock-hour-9" style={{ fontSize: "12px" }} />
          </span>
          {record.production_hr || record.working_hours || "--"}
        </span>
      ),
    },
    {
      title: "Working",
      key: "working_hours",
      width: 80,
      render: (_, record: AttendanceRecord) => (
        <span className="d-inline-flex align-items-center">
          <span
            className="bg-info text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
            style={{
              width: "20px",
              height: "20px",
              minWidth: "20px",
              minHeight: "20px",
              maxWidth: "20px",
              maxHeight: "20px",
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <i className="ti ti-clock-plus" style={{ fontSize: "12px" }} />
          </span>
          {record.working_hours || "--"}
        </span>
      ),
    },
    {
      title: "Break",
      key: "break",
      width: 120,
      render: (_, record: AttendanceRecord) => (
        <span className="d-inline-flex align-items-center">
          <span
            className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
            style={{
              width: "20px",
              height: "20px",
              minWidth: "20px",
              minHeight: "20px",
              maxWidth: "20px",
              maxHeight: "20px",
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <i className="ti ti-clock-pause" style={{ fontSize: "12px" }} />
          </span>
          {record.Break || "1hr"}
        </span>
      ),
    },
    {
      title: "Late / Early",
      key: "lateearly",
      width: 120,
      render: (_, record: AttendanceRecord) => (
        <>
          <div style={{ color: "red" }}>{record.late || "--"}</div>
          <div style={{ color: "orange" }}>{record.earlyout || "--"}</div>
        </>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 80,
      render: (_, record: AttendanceRecord) => (
        <Tag color={statusColors[record.status] || "default"}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record: AttendanceRecord) => (
        <Link
          to={routes.timesheet_admin}
          state={{ employee_id: record.employee_id }} // Pass employee_id via state
          className="btn btn-primary btn-sm"
        >
          <i className="ti ti-eye" style={{ fontSize: "12px" }} />
        </Link>
      ),
    },
  ];

  // Table columns for regularization
  const regularizationColumns: ColumnsType<RegularizationRecord> = [
    {
      title: "Employee",
      key: "employee_id",
      render: (_, record: RegularizationRecord) => {
        const employee = employeeNameMap[record.employee_id];
        const imageUrl =
          employeeImages[record.employee_id] || "/assets/img/defaultavatar.png";

        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt="Employee"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/assets/img/defaultavatar.png";
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{employee?.name || "Unknown Name"}</b>
              </p>
              <span className="fs-12">
                {employee?.role || record.employee_id}
              </span>
            </div>
          </div>
        );
      },
    },
    { title: "Reg Date", dataIndex: "timesheet_date", key: "timesheet_date" },
    { title: "Reg Type", dataIndex: "reg_type", key: "reg_type" },
    { title: "Reason", dataIndex: "reason", key: "reason" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={
            status === "Approved"
              ? "green"
              : status === "Rejected"
              ? "red"
              : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    { title: "Applied Date", dataIndex: "date_apply", key: "date_apply" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: RegularizationRecord) => (
        <div className="d-flex align-items-center gap-2">
          <Button
            type="primary"
            size="small"
            onClick={() => handleApproveClick(record)}
            disabled={record.status !== "Pending"}
            style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
          >
            Approve
          </Button>
          <Button
            size="small"
            danger
            onClick={() => handleRejectClick(record)}
            disabled={record.status !== "Pending"}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  // Event handlers
  const toggleMonthDropdown = () =>
    setIsMonthDropdownOpen(!isMonthDropdownOpen);

  const handleMonthClick = (idx: number) => {
    setSelectedMonth(idx);
    setIsMonthDropdownOpen(false);
  };

  const handleDatePickerChange = (date: Dayjs | null) => {
    setSelectedDate(date);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Attendance Dashboard</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Dashboard
                </li>
              </ol>
            </nav>
          </div>

          {/* Filters */}
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            {/* <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => navigate(all_routes.AttendanceTablePage)}
                style={{
                      height: "34px",
                      minWidth: "120px",
                    }}
              >
                Attendance Table
              </button> */}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "month" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => {
                  setViewMode("month");
                  setSelectedDate(dayjs());
                }}
              >
                Month
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "date" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setViewMode("date")}
              >
                Date
              </button>
            </div>

            {viewMode === "date" ? (
              <ReactDatePicker
                selected={selectedDate ? selectedDate.toDate() : null}
                onChange={(date) => setSelectedDate(dayjs(date))}
                customInput={
                  <button
                    className="btn btn-primary d-flex align-items-center justify-content-center"
                    style={{
                      height: "34px",
                      minWidth: "160px",
                      padding: "0 12px",
                    }}
                  >
                    <i className="ti ti-calendar me-2" />
                    {selectedDate
                      ? selectedDate.format("DD-MM-YYYY")
                      : "Select Date"}
                  </button>
                }
                dateFormat="dd-MM-YYYY"
                popperPlacement="bottom-start"
              />
            ) : (
              <div className="position-relative" style={{ minWidth: "160px" }}>
                <button
                  onClick={toggleMonthDropdown}
                  className="btn btn-outline-primary d-flex align-items-center justify-content-between w-100"
                  style={{
                    height: "34px",
                    padding: "0 12px",
                  }}
                >
                  <i className="ti ti-calendar-month me-2" />
                  {monthNames[selectedMonth]} - {selectedYear}
                  <i className="ti ti-chevron-down ms-auto" />
                </button>

                {isMonthDropdownOpen && (
                  <div
                    className="dropdown-menu show p-3 shadow position-absolute bg-white"
                    style={{
                      top: "42px",
                      minWidth: "280px",
                      zIndex: 1000,
                      borderRadius: "8px",
                    }}
                  >
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Select Month:
                      </label>
                      <div className="d-flex flex-wrap gap-1">
                        {monthNames.map((month, idx) => (
                          <button
                            key={month}
                            onClick={() => handleMonthClick(idx)}
                            className={`btn btn-sm ${
                              selectedMonth === idx
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            style={{ fontSize: "11px", padding: "4px 8px" }}
                          >
                            {month}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="form-label fw-semibold">Year:</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(+e.target.value)}
                        min={2020}
                        max={2030}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <CollapseHeader />
          </div>
        </div>

        {/* Attendance Widgets */}
        <div className="card mb-4 position-relative">
          {loading ? (
            <div className="text-center py-5">
              <Spin size="large" />
            </div>
          ) : (
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                  <small className="text-muted">
                    <i className="ti ti-calendar me-1"></i>
                    Filtered by:{" "}
                    {selectedDate
                      ? selectedDate.format("DD-MM-YYYY")
                      : `${monthNames[selectedMonth]} ${selectedYear}`}
                  </small>
                </div>
                <small className="text-muted">
                  Total Metrics: {attendanceWidgetData.length}
                </small>
              </div>
              {attendanceWidgetData.length > 0 && (
                <>
                  <button
                    className="btn btn-dark btn-sm position-absolute top-50 start-0 translate-middle-y shadow"
                    style={{ zIndex: 1 }}
                    onClick={() => scrollWidgets("left")}
                    disabled={!canScrollLeft}
                  >
                    <i className="ti ti-chevron-left"></i>
                  </button>
                  <button
                    className="btn btn-dark btn-sm position-absolute top-50 end-0 translate-middle-y shadow"
                    style={{ zIndex: 1 }}
                    onClick={() => scrollWidgets("right")}
                    disabled={!canScrollRight}
                  >
                    <i className="ti ti-chevron-right"></i>
                  </button>
                </>
              )}
              <div
                className="d-flex overflow-auto"
                ref={scrollRef}
                style={{
                  gap: "1rem",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: "0.5rem",
                }}
              >
                {attendanceWidgetData.map((widget) => (
                  <div
                    key={widget.id}
                    className="card flex-shrink-0"
                    style={{ minWidth: "260px", maxWidth: "260px" }}
                  >
                    <div className="card-body">
                      <h6 className="mb-2">{widget.title}</h6>
                      <div className="d-flex align-items-center mb-1">
                        <span
                          className={`bg-${widget.color} text-white rounded me-2`}
                          style={{
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            backgroundColor: widget.bg,
                          }}
                        >
                          <i className={widget.icon}></i>
                        </span>
                        <span className="text-muted small">Total</span>
                      </div>
                      <h3 className="mb-0">{widget.totalApplied}</h3>
                      <div className="d-flex gap-3 mt-2">
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-success rounded-circle me-1"
                            style={{ width: "8px", height: "8px" }}
                          ></div>
                          <small className="text-muted">
                            Total Employee: {widget.totalEmployees}
                          </small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-danger rounded-circle me-1"
                            style={{ width: "8px", height: "8px" }}
                          ></div>
                          <small className="text-muted">
                            Present: {widget.presentEmployees}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Shift Count and Regularization Widget */}
        <div className="row my-4">
          <div className="col-lg-6">
            <div className="card rounded-lg shadow-sm border-0 h-100">
              <div className="card-header bg-transparent border-0 pb-0">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0 fw-semibold">Shift Count</h5>
                    <small className="text-muted">
                      Assigned Employees Count
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body pt-2">
                <div
                  className="d-flex flex-column gap-2"
                  style={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    paddingRight: "8px",
                  }}
                >
                  {pendingRequestsData.length > 0 ? (
                    pendingRequestsData.map((item, index) => (
                      <div key={index} className="p-3 border rounded bg-light">
                        <h6 className="mb-3 fw-semibold">{item.title}</h6>
                        <div className="d-flex align-items-center mb-2 gap-3">
                          <div className="d-flex align-items-center">
                            <span
                              className={`bg-${item.color} text-white rounded d-flex align-items-center justify-content-center me-2`}
                              style={{
                                width: "24px",
                                height: "24px",
                                fontSize: "14px",
                              }}
                            >
                              <i className={item.iconClass}></i>
                            </span>
                            <div>
                              <span className="text-muted small">
                                Total Employees
                              </span>
                              <h4 className="mb-0 fw-bold">
                                {item.totalEmployees}
                              </h4>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-5">
                            <div style={{ width: "8px", height: "8px" }}></div>
                            <div>
                              <span className="text-muted small">Present</span>
                              <h4 className="mb-0 fw-bold text-success">
                                {item.present}
                              </h4>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-5">
                            <div style={{ width: "8px", height: "8px" }}></div>
                            <div>
                              <span className="text-muted small">Absent</span>
                              <h4 className="mb-0 fw-bold text-danger">
                                {item.absent}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted py-3">
                      No Shift Data available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card rounded-lg shadow-sm border-0 h-100">
              <div className="card-header bg-transparent border-0 pb-0">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0 fw-semibold">Regularization Widget</h5>
                    <small className="text-muted">
                      Monthly Status Overview
                    </small>
                  </div>
                </div>
              </div>
              <div className="card-body pt-2">
                <div
                  className="d-flex flex-column gap-2"
                  style={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    paddingRight: "8px",
                  }}
                >
                  {regularizationWidgetData.length > 0 ? (
                    regularizationWidgetData.map((item, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between p-3 border rounded bg-light"
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle me-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: item.color,
                              fontSize: "16px",
                            }}
                          >
                            {item.icon}
                          </div>
                          <div>
                            <h6 className="mb-0 fw-semibold">{item.title}</h6>
                            <small className="text-muted">Regularization</small>
                          </div>
                        </div>
                        <div className="text-end">
                          <span
                            className="badge rounded-pill px-3 py-2 text-white fw-semibold d-inline-flex align-items-center"
                            style={{ backgroundColor: item.color }}
                          >
                            {item.count}
                            <i
                              className="ti ti-arrow-right text-dark ms-2"
                              style={{ cursor: "pointer", fontSize: "1.0rem" }}
                              onClick={() => handlePendingView(item.type)}
                            ></i>
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted py-3">
                      No Regularization Data available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Table */}
        <div className="card" ref={tableRef}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                className="mb-0"
              >
                <TabPane tab="Attendance Records" key="1" />
                <TabPane tab="Regularization" key="2" />
              </Tabs>
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                <Dropdown className="flex-grow-0" style={{ width: 220 }}>
                  <Dropdown.Toggle
                    variant="white"
                    className="border"
                    style={{
                      height: "34px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "0 10px",
                    }}
                  >
                    Department: {selectedDepartment}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {departments.map((dept) => (
                      <Dropdown.Item
                        key={dept}
                        onClick={() => setSelectedDepartment(dept)}
                      >
                        {dept}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <Input.Search
                  placeholder={
                    activeTab === "1"
                      ? "Search attendance..."
                      : "Search regularization..."
                  }
                  allowClear
                  onChange={(e) =>
                    activeTab === "1"
                      ? setSearchText(e.target.value)
                      : setRegularizationSearchText(e.target.value)
                  }
                  style={{
                    width: 200,
                    height: "44px",
                    display: "flex",
                    alignItems: "center",
                  }}
                />
              </div>
            </div>
            <div style={{ width: "100%", margin: "0 auto", overflowX: "auto" }}>
              {activeTab === "1" ? (
                <Table
                  columns={attendanceColumns}
                  dataSource={filteredAttendanceData}
                  bordered
                  rowKey="id"
                  loading={loading}
                />
              ) : (
                <Table
                  columns={regularizationColumns}
                  dataSource={filteredRegDataByStatus(
                    regularizationWidgetData.find(
                      (item) =>
                        item.type === regularizationSearchText ||
                        item.type === "All"
                    )?.type || "All"
                  )}
                  bordered
                  rowKey="id"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Reject Request"
        open={rejectModalOpen}
        onOk={handleRejectSubmit}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason("");
          setSelectedRecord(null);
        }}
        okText="Submit"
        cancelText="Cancel"
      >
        <p>Please provide a reason for rejection:</p>
        <TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={4}
          placeholder="Enter reason..."
        />
      </Modal>
    </div>
  );
};
export default TimeSheetDash;
